import { useState, useEffect } from 'react';
import Empty from 'constants/empty';
import { CONSTRUCTOR_PODS } from 'constants/constructorConstants';
import getProductRecommendations from 'services/api/productRecommendations/getProductRecommendations';
import buildBundleProducts from 'components/ProductPage/PdpBundling/BundleWidget/utils/buildBundleProducts';
import logError from 'utils/framework/logError';

const NUM_RESULTS = 2;

/**
 * FER-819 — fetches the automated (Constructor.io) bundle product list via the
 * PRS GraphQL ProductRecommendations proxy and maps it to the manual-path shape.
 *
 * Fetch runs browser-side only (relies on window.ConstructorioClient), keeping SSR safe.
 * AC-2: on GraphQL error or empty results the bundle stays empty so the PDP renders normally.
 *
 * @param {object} params
 * @param {boolean} params.isAutomated - true when productBundleDataSource === Automated.
 * @param {string} params.productId - seed product id (currentProduct.productDetails.productId).
 * @returns {{ bundleProducts: Array }}
 */
const useBundleRecommendations = ({ isAutomated, productId } = Empty.Object) => {
    const [result, setResult] = useState({ productId: undefined, bundleProducts: Empty.Array });

    useEffect(() => {
        if (!isAutomated || !productId) {
            return undefined;
        }

        let isActive = true;

        const fetchBundle = async () => {
            try {
                const { recommendations } = await getProductRecommendations({
                    podId: CONSTRUCTOR_PODS.PDP_BUNDLING,
                    itemIds: [productId],
                    numResults: NUM_RESULTS
                });

                if (isActive) {
                    setResult({ productId, bundleProducts: buildBundleProducts(recommendations) });
                }
            } catch (error) {
                // AC-2: log for observability, never surface to the customer.
                logError(error, { source: 'useBundleRecommendations' });

                if (isActive) {
                    setResult({ productId, bundleProducts: Empty.Array });
                }
            }
        };

        fetchBundle();

        return () => {
            isActive = false;
        };
    }, [isAutomated, productId]);

    const bundleProducts = result.productId === productId ? result.bundleProducts : Empty.Array;

    return { bundleProducts };
};

export default useBundleRecommendations;
